import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkProgressBar } from "uitk-react-progress-bar";
import { UitkCard, UitkCardContent } from "uitk-react-cards";
import { UitkLink } from "uitk-react-link";

import { FlexClickTracker } from "src/components/utility/analytics/FlexClickTracker/FlexClickTracker";

import { SampleReviewProps, DestinationSampleReviewsProps } from "../typings";

const MAX_NUM_REVIEWS_DISPLAY = 6;

const DestinationSampleReviews = (props: DestinationSampleReviewsProps) => {
  const {
    title,
    nameForTracking,
    reviews,
    destinationName,
    totalReviews,
    totalRatings,
    countPerRating,
    avgRating,
    toggleAllDestinationReviews
  } = props;
  const { formatText } = useLocalization();
  const totalReviewsLocalized = totalReviews.toLocaleString();
  const sampleReviews = reviews.filter(review => review.rating).slice(0, MAX_NUM_REVIEWS_DISPLAY);

  const ratingDescriptor =
    avgRating >= 4 ? formatText("destinationReviews.topDestination") : formatText("destinationReviews.guestRating");

  const ButtonTracked = FlexClickTracker(UitkSecondaryButton);
  const LinkTracked = FlexClickTracker(UitkLink);

  delete countPerRating["0"];
  const ratingBars = Object.values(countPerRating)
    .map((rating, index) => {
      const percentage = (rating * 100) / totalRatings;
      const ratingScore = index + 1;

      return (
        <div className="uitk-cell all-b-padding-two" key={index}>
          <UitkProgressBar
            percent={percentage}
            progressDescription={`${rating}`}
            title={`${ratingScore} - ${formatText(`destinationReviews.descriptor.${ratingScore}`)}`}
          />
        </div>
      );
    })
    .reverse();

  return (
    <div className="DestinationSampleReviews uitk-cell all-y-padding-six all-x-padding-six">
      <div itemScope={true} itemType="http://schema.org/AggregateRating" itemProp="aggregateRating">
        <meta itemProp="reviewCount" content={String(totalReviews)} />
        <meta itemProp="ratingValue" content={String(avgRating)} />
      </div>

      <h2 className="uitk-type-heading-600 all-b-margin-four">
        {title || formatText("destinationReviews.cityReviews", destinationName)}
      </h2>

      <div className="uitk-grid all-x-gutter-six">
        <div className="uitk-cell all-cell-1-3 m-cell-1-1 s-cell-1-1">
          <div className="uitk-flex">
            <p className="uitk-type-900">{avgRating}</p>

            <div className="uitk-type-500 all-l-padding-three">
              <p className="uitk-cell uitk-type-bold all-t-padding-one">{ratingDescriptor}</p>
              <div className="uitk-type-300">
                <LinkTracked
                  rfrr="Detail.OpenReviews"
                  onClick={toggleAllDestinationReviews}
                  moduleName={nameForTracking}
                  linkName="RFRR Action Event"
                >
                  <button>{formatText("destinationReviews.numberOfReviews", totalReviewsLocalized)}</button>
                </LinkTracked>
              </div>
            </div>
          </div>

          <div className="uitk-cell all-t-padding-three">{ratingBars}</div>
        </div>

        <div className="DestinationUserReviewsContainer uitk-cell all-cell-2-3 m-cell-1-1 s-cell-1-1">
          {sampleReviews.map((review, i: number) => {
            const isLast = i === sampleReviews.length - 1;

            return <SampleReview destinationReview={review} showBorder={!isLast} key={review.postStayReviewId} />;
          })}
          <ButtonTracked
            text={formatText("destinationReviews.seeAllCityReviews", totalReviewsLocalized, destinationName)}
            onClick={toggleAllDestinationReviews}
            className="all-t-margin-three"
            rfrr="Detail.OpenReviews"
            moduleName={nameForTracking}
            linkName="RFRR Action Event"
          />
        </div>
      </div>
    </div>
  );
};

const SampleReview = (props: SampleReviewProps) => {
  const { review, rating, createTime, reviewerDisplayName } = props.destinationReview;
  const date = new Date(createTime);
  const { formatText, formatDateString } = useLocalization();

  return (
    <UitkCard>
      <UitkCardContent className={props.showBorder ? "uitk-card-separator-bottom" : ""}>
        <article className="all-y-padding-six">
          {rating && (
            <h3 className="uitk-type-heading-500 all-b-padding-three">{`${rating}/5 - ${formatText(
              `destinationReviews.descriptor.${rating}`
            )}`}</h3>
          )}

          <div className="all-b-padding-two">
            <p className="uitk-type-heading-300">
              {reviewerDisplayName || formatText("destinationReviews.verified.traveler")}
            </p>

            <span className="uitk-type-300">{date && formatDateString(date, { skeleton: "yMMMd" })}</span>
          </div>

          <div className="review-text">
            <p className="uitk-type-paragraph-300">{review}</p>
          </div>
        </article>
      </UitkCardContent>
    </UitkCard>
  );
};

export default DestinationSampleReviews;
